import { useCallback, useEffect, useState } from "react";
import { EyeOff, Loader2, Ticket } from "lucide-react";
import { adminCreateCoupon, adminGetCoupons, adminToggleCoupon, formatApiError, inr } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

const inputCls =
  "rounded-md border border-input bg-white/60 px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary transition";

const EMPTY_FORM = { code: "", type: "percent", value: "", min_order: "", hidden: false, base: "sale" };

export const CouponsTab = ({ token }) => {
  const [coupons, setCoupons] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setCoupons(await adminGetCoupons(token));
    } catch {
      toast.error("Could not load coupons");
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await adminCreateCoupon(token, {
        code: form.code.trim(),
        type: form.type,
        value: Number(form.value),
        min_order: Number(form.min_order) || 0,
        active: true,
        hidden: form.hidden,
        base: form.type === "percent" ? form.base : "sale",
      });
      toast.success(
        form.hidden
          ? `Private coupon ${form.code.toUpperCase()} created — share it personally, it won't show on the store`
          : `Coupon ${form.code.toUpperCase()} created`
      );
      setForm(EMPTY_FORM);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (c) => {
    await adminToggleCoupon(token, c.id, !c.active);
    setCoupons((prev) => prev.map((x) => (x.id === c.id ? { ...x, active: !x.active } : x)));
  };

  return (
    <div className="grid lg:grid-cols-3 gap-8" data-testid="coupons-tab">
      <form onSubmit={create} className="bg-white/70 border border-border/60 rounded-xl p-6 h-fit" data-testid="coupon-form">
        <p className="font-serif text-lg font-semibold flex items-center gap-2">
          <Ticket className="w-4 h-4" /> New coupon
        </p>
        <div className="space-y-3 mt-4">
          <input
            data-testid="coupon-code-input"
            required
            value={form.code}
            onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
            placeholder="CODE e.g. FAMILY25"
            className={`${inputCls} w-full font-mono uppercase`}
          />
          <div className="grid grid-cols-2 gap-2">
            <select
              data-testid="coupon-type-select"
              value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value })}
              className={inputCls}
            >
              <option value="percent">% off</option>
              <option value="flat">₹ flat off</option>
            </select>
            <input
              data-testid="coupon-value-input"
              required
              type="number"
              min="1"
              value={form.value}
              onChange={(e) => setForm({ ...form, value: e.target.value })}
              placeholder={form.type === "percent" ? "% e.g. 10" : "₹ e.g. 100"}
              className={inputCls}
            />
          </div>
          {form.type === "percent" && (
            <div>
              <select
                data-testid="coupon-base-select"
                value={form.base}
                onChange={(e) => setForm({ ...form, base: e.target.value })}
                className={`${inputCls} w-full`}
              >
                <option value="sale">Discount on sale price</option>
                <option value="mrp">Discount on MRP (family &amp; friends)</option>
              </select>
              {form.base === "mrp" && (
                <p className="text-[11px] text-muted-foreground mt-1.5 leading-snug">
                  The % is calculated on the full MRP value, not the discounted sale price — a bigger saving for special people. Discount never exceeds the payable amount.
                </p>
              )}
            </div>
          )}
          <input
            data-testid="coupon-min-input"
            type="number"
            min="0"
            value={form.min_order}
            onChange={(e) => setForm({ ...form, min_order: e.target.value })}
            placeholder="Min. order ₹ (0 = none)"
            className={`${inputCls} w-full`}
          />
          <label className="flex items-start gap-2.5 cursor-pointer rounded-lg border border-dashed border-border px-3.5 py-3" data-testid="coupon-private-toggle">
            <input
              type="checkbox"
              checked={form.hidden}
              onChange={(e) => setForm({ ...form, hidden: e.target.checked })}
              className="w-4 h-4 mt-0.5 accent-[#3f5426]"
            />
            <span className="text-xs font-semibold leading-snug">
              Private coupon
              <span className="block text-[11px] font-normal text-muted-foreground mt-0.5">
                Never shown anywhere on the store — share it personally with family, friends or special customers. It works only when typed in at checkout.
              </span>
            </span>
          </label>
          <button
            data-testid="coupon-create-button"
            disabled={busy}
            className="w-full rounded-full bg-primary text-primary-foreground py-3 text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Create coupon
          </button>
        </div>
      </form>

      <div className="lg:col-span-2 space-y-3" data-testid="coupons-list">
        {coupons.length === 0 && (
          <p className="text-muted-foreground text-sm py-10 text-center">No coupons yet — create your first one.</p>
        )}
        {coupons.map((c) => (
          <div key={c.id} data-testid={`coupon-${c.code}`} className="bg-white/70 border border-border/60 rounded-xl p-5 flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="font-mono font-bold text-lg flex items-center gap-2.5">
                {c.code}
                {c.hidden && (
                  <span
                    data-testid={`coupon-private-badge-${c.code}`}
                    className="inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.15em] font-bold bg-amber-100 text-amber-800 rounded-full px-2.5 py-0.5"
                  >
                    <EyeOff className="w-3 h-3" /> Private
                  </span>
                )}
                {c.base === "mrp" && c.type === "percent" && (
                  <span
                    data-testid={`coupon-mrp-badge-${c.code}`}
                    className="inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.15em] font-bold bg-primary/10 text-primary rounded-full px-2.5 py-0.5"
                  >
                    On MRP
                  </span>
                )}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {c.type === "percent" ? `${c.value}% off` : `${inr(c.value)} off`}
                {c.base === "mrp" && c.type === "percent" ? " of MRP value" : ""}
                {c.min_order > 0 ? ` · min order ${inr(c.min_order)}` : " · no minimum"}
                {c.hidden ? " · not shown on store" : " · shown in store offers"}
              </p>
            </div>
            <button
              data-testid={`coupon-toggle-${c.code}`}
              onClick={() => toggle(c)}
              className={`rounded-full px-4 py-2 text-xs font-bold transition-colors ${
                c.active ? "bg-primary/10 text-primary" : "bg-secondary text-muted-foreground"
              }`}
            >
              {c.active ? "Active — tap to pause" : "Paused — tap to activate"}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};
